import React from "react";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { CompanyMark } from "../../../common/u1/CompanyMark";
import { codeToFlagEmoji } from "../../../common/u1/countryFlag";
import { Aed } from "../../../common/Money";
import { WEB_PROFILE_MEMBER } from "../../../../constants/webProfilePricing";
import {
  companyCountryCode,
  countryNameOf,
  summaryCompanyName,
} from "./paymentDetailsFields";

/* What is being bought, what it costs, and the one upsell. */
export const OrderSummaryPanel = ({
  company,
  summary,
  fallbackName,
  upsell = false,
  onSwitchToMembership,
  className = "",
}) => {
  const companyName = summaryCompanyName(company, fallbackName);
  const companyId = company?.companyID || "";
  const code = companyCountryCode(company);
  const countryName = countryNameOf(code);

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <section
        aria-labelledby="u1-pd-summary-heading"
        className="rounded-[14px] border border-[var(--line)] bg-white p-4 sm:p-6"
      >
        <h2
          id="u1-pd-summary-heading"
          className="m-0 text-[11px] font-bold uppercase tracking-[.11em] text-[var(--muted)] font-sans"
        >
          You&apos;re buying claim to
        </h2>

        <div className="mt-3 flex items-center gap-3 border-b border-[var(--line)] pb-4">
          <CompanyMark company={{ name: companyName, companyID: companyId }} size="sm" />
          <div className="min-w-0">
            <p className="m-0 truncate text-[14px] font-bold text-[var(--ink)] font-sans">
              {companyName}
            </p>
            <p className="m-0 mt-0.5 truncate text-[12px] text-[var(--muted)] font-sans">
              {code && <span className="mr-1">{codeToFlagEmoji(code)}</span>}
              {countryName}
              {countryName && companyId ? " · " : ""}
              {companyId}
            </p>
          </div>
        </div>

        <dl className="m-0 flex flex-col gap-2.5 py-4">
          {summary?.lines?.map((line) => (
            <div key={line.key} className="flex items-baseline justify-between gap-3">
              <dt
                className={cn(
                  "text-[13px] font-sans",
                  line.savings ? "text-[var(--success)]" : "text-[var(--ink-2)]"
                )}
              >
                {line.label}
                {line.sublabel && (
                  <span className="block text-[11.5px] text-[var(--muted)]">
                    {line.sublabel}
                  </span>
                )}
              </dt>
              <dd
                className={cn(
                  "m-0 inline-flex items-center gap-2 text-[13.5px] font-semibold font-sans",
                  line.savings ? "text-[var(--success)]" : "text-[var(--ink)]"
                )}
              >
                {line.strike && (
                  <span className="font-normal text-[var(--muted)] line-through">
                    <Aed value={line.strike} />
                  </span>
                )}
                <Aed value={line.value} />
              </dd>
            </div>
          ))}

          {/* TIP-646 — the VAT line is drawn only once Stripe has QUOTED it.
              Before the billing address is known there is no honest figure to
              show, so the summary stays ex-VAT and says so beneath. Guessing a
              flat 5% here is what charged an overseas buyer's screen for tax
              they were never billed. */}
          {summary?.quoted && (
            <div className="flex items-baseline justify-between gap-3">
              <dt className="text-[13px] text-[var(--muted)] font-sans">VAT</dt>
              <dd className="m-0 text-[13.5px] text-[var(--ink-2)] font-sans">
                <Aed value={summary?.vat ?? 0} />
              </dd>
            </div>
          )}
        </dl>

        {!summary?.quoted && (
          <p className="m-0 mb-3 text-[11.5px] leading-[1.5] text-[var(--muted)] font-sans">
            VAT is calculated at checkout from your billing address.
          </p>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-[var(--line)] pt-3.5">
          <span className="text-[13.5px] font-bold text-[var(--ink)] font-sans">
            {summary?.quoted ? "Total" : "Total (excl. VAT)"}
          </span>
          <span className="text-[17px] font-bold text-[var(--ink)] font-sans">
            <Aed value={summary?.total ?? 0} />
          </span>
        </div>

        {summary?.renewalNote && (
          <p className="m-0 mt-3 text-[11.5px] leading-[1.5] text-[var(--muted)] font-sans">
            {summary.renewalNote}
          </p>
        )}
      </section>

      {/* Offered on a standard-rate purchase only — see showsUpsell. */}
      {upsell && (
        <section className="rounded-[14px] bg-[var(--navy)] p-4 sm:p-6">
          <h2 className="m-0 text-[14px] font-bold text-white font-sans">
            Buying more than one this year?
          </h2>
          <p className="m-0 mt-1.5 inline-flex flex-wrap items-center gap-1 text-[12.5px] leading-[1.55] text-white/70 font-sans">
            Members pay <Aed value={WEB_PROFILE_MEMBER} /> on every Web Profile, plus
            Payment Endorsements, the Partner Program, and the path to Verified Member.
          </p>
          <Button variant="onDark" className="mt-3.5 w-full" onClick={onSwitchToMembership}>
            Switch to Membership
          </Button>
        </section>
      )}
    </div>
  );
};

export default OrderSummaryPanel;
